import { useEffect, useState } from "react";
import { Form, InputNumber, Modal, Skeleton } from "antd";
import { useNavigate } from "react-router-dom";

import { cotaService } from "../../../services/cotaService";
import { atualizarCotaService } from "../../../services/atualizarCotaService";
import { Cota } from "../../../interfaces/Cota";
import { layout } from "../styles";
import { useMessage } from "../../../uteis/mensagens/useMessage";

interface ModalEditarProps {
  isModalOpen: boolean;
  handleFechar: () => void;
  cotaId: number | null;
}

export default function ModalEditar({
  isModalOpen,
  handleFechar,
  cotaId,
}: ModalEditarProps) {
  const [form] = Form.useForm();
  const navigate = useNavigate();
  const { showError, showSuccess, contextHolder } = useMessage();
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [cota, setCota] = useState<Partial<Cota>>({});

  useEffect(() => {
    const buscarCota = async () => {
      if (!isModalOpen || !cotaId) {
        return;
      }

      setIsLoading(true);

      try {
        const data = await cotaService(cotaId);

        if (!data) {
          showError("Nao foi possivel carregar a cota.");
          return;
        }

        const cotaAtual = {
          id: data.id,
          aptoId: data.aptoId,
          numeroCota: data.numeroCota,
          origem: data.origem,
          diaDaSemanaInicio: data.diaDaSemanaInicio,
          qtdSemanas: data.qtdSemanas,
        };

        setCota(cotaAtual);
        form.setFieldsValue({
          numeroCota: cotaAtual.numeroCota,
          origem: cotaAtual.origem,
        });
      } finally {
        setIsLoading(false);
      }
    };

    buscarCota();
  }, [cotaId, form, isModalOpen, showError]);

  useEffect(() => {
    if (!isModalOpen) {
      form.resetFields();
      setCota({});
    }
  }, [form, isModalOpen]);

  const handleValuesChange = (_changedValues: any, allValues: Partial<Cota>) => {
    setCota((prevState) => ({
      ...prevState,
      ...allValues,
    }));
  };

  const handleAtualizar = async () => {
    if (!cotaId) {
      return;
    }

    try {
      const values = await form.validateFields();
      setIsSaving(true);

      const response = await atualizarCotaService(cotaId, {
        ...cota,
        ...values,
      });

      if (!response) {
        showError("Nao foi possivel atualizar a cota.");
        return;
      }

      showSuccess("Cota atualizada com sucesso!");
      setTimeout(() => {
        navigate(0);
      }, 1200);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Modal
      open={isModalOpen}
      onCancel={handleFechar}
      onOk={handleAtualizar}
      okText="Atualizar"
      cancelText="Cancelar"
      confirmLoading={isSaving}
    >
      {contextHolder}
      {isLoading ? (
        <Skeleton active />
      ) : (
        <Form
          {...layout}
          form={form}
          name="editar-cota"
          onValuesChange={handleValuesChange}
        >
          <h1>Editar Cota</h1>
          <Form.Item
            name="numeroCota"
            label="Numero da Cota"
            className="formItems"
            rules={[
              {
                required: true,
                message: "Informe o numero da cota",
              },
            ]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
          <Form.Item
            name="origem"
            label="Origem"
            className="formItems"
            rules={[
              {
                required: true,
                message: "Informe a origem da cota",
              },
            ]}
          >
            <InputNumber min={0} style={{ width: "100%" }} />
          </Form.Item>
        </Form>
      )}
    </Modal>
  );
}
